import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { ARROW_DOWN, ARROW_UP } from 'approot/shared/ally/keyboard.hooks';
import { SortResult } from './sortable.constants';
import { SortableCard } from './sortable-card';
import { SortableCardHandle } from './sortable-card.style';
import ActivityBuilderInlineSvg from '../inline-svg/activity-builder/activity-builder-inline-svg';
import * as Styled from './sortable.style';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

type Props<T extends { sort: string | null }> = {
  list: T[];
  renderItem: (item: T, index: number) => ReactNode;
  onSortChange: (item: SortResult) => void;
};

export function Sortable<T extends { sort: string | null }>({
  list,
  renderItem,
  onSortChange,
}: Props<T>) {
  const [localList, setLocalList] = useState<T[]>(list.map(i => i));

  const move = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const [reordered] = localList.splice(sourceIndex, 1);
      localList.splice(destinationIndex, 0, reordered);
      setLocalList([...localList]);
    },
    [localList]
  );

  const handleKeyDownMove = useCallback(
    (e: KeyboardEvent<HTMLDivElement>, sourceIndex: number) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains('sortable-handle')) return;

      const key = e.key;
      if (key === ARROW_DOWN && sourceIndex + 1 < localList.length) {
        e.preventDefault();
        e.stopPropagation();
        move(sourceIndex, sourceIndex + 1);
        onSortChange({
          sourceIndex: sourceIndex,
          destinationIndex: sourceIndex + 1,
          direction: DIRECTION_RIGHT,
        });
        return;
      }

      if (key === ARROW_UP && sourceIndex - 1 >= 0) {
        e.preventDefault();
        e.stopPropagation();
        move(sourceIndex, sourceIndex - 1);
        onSortChange({
          sourceIndex: sourceIndex,
          destinationIndex: sourceIndex - 1,
          direction: DIRECTION_LEFT,
        });
        return;
      }
    },
    [localList, move, onSortChange]
  );

  useEffect(() => {
    setLocalList(list.slice(0));
    //eslint-disable-next-line
  }, [list.map(l => l.sort).join(''), setLocalList]);

  return (
    <>
      {localList.map((item, index) => (
        <SortableCard
          key={item.sort}
          sourceIndex={index}
          index={index}
          move={move}
          handleMoveWithKeyDown={handleKeyDownMove}
          onDrop={onSortChange}
          onDidNotDrop={() => {
            setLocalList(list.slice(0));
          }}
        >
          {renderItem(item, index)}
        </SortableCard>
      ))}
    </>
  );
}

export const SortableHandle = ({
  isHorizontal = true,
}: {
  isHorizontal?: boolean;
}) => (
  <SortableCardHandle className="sortable-handle">
    <ActivityBuilderInlineSvg
      id="icon_grab_empty"
      width={16}
      height={20}
      $rotate={isHorizontal ? 90 : 0}
    />
  </SortableCardHandle>
);

export const SortableHeader = ({ children }: { children?: ReactNode }) => (
  <Styled.SortableHeader>
    <SortableHandle />
    {children}
  </Styled.SortableHeader>
);

export const SortableItem = ({ children }: { children?: ReactNode }) => (
  <Styled.SortableItem>{children}</Styled.SortableItem>
);

export const SortableContent = ({ children }: { children: ReactNode }) => (
  <Styled.SortableContent>{children}</Styled.SortableContent>
);

export const SortableItemButton = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <Styled.SortableContentButton onClick={onClick}>
    <div>{children}</div>
    <IconDropdown width={16} height={16} />
  </Styled.SortableContentButton>
);
